import type { FulfillmentReason } from '../domain/fulfillment';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

export class ValidationError extends ApiError {
  constructor(message: string) {
    super(message, 'VALIDATION');
  }
}

export class ConflictError extends ApiError {
  constructor(message: string, readonly latestVersion?: number) {
    super(message, 'CONFLICT');
  }
}

export class DuplicateError extends ApiError {
  constructor(message: string) {
    super(message, 'DUPLICATE');
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string) {
    super(message, 'NOT_FOUND');
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string) {
    super(message, 'FORBIDDEN');
  }
}

// Stable reasons leatherback's ShipmentsService prefixes Cargonizer booking
// errors with ("REASON: details") -- see shipments.proto.
export const SHIPMENT_ERROR_REASONS = [
  'INVALID_ADDRESS',
  'REVERSED_ENDPOINTS',
  'UNSUPPORTED_PRODUCT',
  'PROVIDER_REJECTED',
  'PROVIDER_AUTH_FAILED',
  'PROVIDER_UNAVAILABLE',
  'NOT_PERSISTED',
  'MISSING_MEASUREMENTS',
] as const;

export type ShipmentErrorReason = (typeof SHIPMENT_ERROR_REASONS)[number];

// A shipment booking error with a stable reason as its code.
export class ShipmentBookingError extends ApiError {
  constructor(
    message: string,
    readonly reason: ShipmentErrorReason,
  ) {
    super(message, reason);
  }
}

export function parseShipmentErrorReason(
  message: string,
): { reason: ShipmentErrorReason; detail: string } | undefined {
  const match = /^([A-Z_]+):\s*(.*)$/s.exec(message);
  const reason = match?.[1] as ShipmentErrorReason | undefined;
  if (!match || !reason || !SHIPMENT_ERROR_REASONS.includes(reason)) return undefined;
  return { reason, detail: match[2] ?? '' };
}

/**
 * A rejection with one of the fulfilment backend's stable reasons (the
 * "REASON: detail" message prefix), e.g. WRONG_ITEM for a bad scan.
 */
export class FulfillmentError extends ApiError {
  constructor(
    message: string,
    readonly reason: FulfillmentReason,
  ) {
    super(message, 'FULFILLMENT');
  }

  /** The server's explanation after "REASON: ", e.g. "VVCS-0001 is now SCRAP". */
  get detail(): string {
    const prefix = `${this.reason}: `;
    return this.message.startsWith(prefix) ? this.message.slice(prefix.length) : '';
  }
}

/** A device summary run refused because the last one was too recent; retry with force. */
export class RunTooSoonError extends ApiError {
  constructor(message: string) {
    super(message, 'RUN_TOO_SOON');
  }
}
